import { Big } from 'big.js';
import { bigToString, QuoteService } from 'quote-domain-core';
import { QuoteServiceArticlePricingService } from './quote-service-article-pricing-service';
import { QuoteServiceCorridorAggregationService } from './quote-service-corridor-aggregation-service';
import { QuoteServiceDiscountService } from './quote-service-discount-service';
import { CorridorService } from '../corridor/corridor-service';
import { PricingItemPriceService } from '../pricing-item/pricing-item-price-service';
import { singleton } from 'tsyringe';

export type QuoteServiceLifecycleCompletionOptions = {
    resetDistance?: boolean;
    updateLastAppliedPriceState?: boolean;
    updateLastContractInfoInputs?: boolean;
};

@singleton()
export class QuoteServiceLifecycleService {
    constructor(
        private readonly articlePricingService: QuoteServiceArticlePricingService,
        private readonly corridorAggregationService: QuoteServiceCorridorAggregationService,
        private readonly discountService: QuoteServiceDiscountService,
        private readonly corridorService: CorridorService,
        private readonly pricingItemPriceService: PricingItemPriceService
    ) {
    }

    public calculateLastServiceDistance(quoteService: QuoteService): void {
        if (quoteService.globalCorridorOnlyNegociable == null) {
            const result = this.corridorAggregationService.sumDevicesAndConsumablesAppliedPrices(quoteService);
            const appliedPriceOnlyNegociable = new Big(quoteService.appliedPrice).minus(
                result.appliedPriceOnlyNonNegociableConsumables.plus(result.appliedPriceOnlyNonNegociableDevices)
            );
            this.corridorAggregationService.getJoinCorridorNegotiable(quoteService);
            Object.assign(quoteService, { appliedPriceOnlyNegociable: bigToString(appliedPriceOnlyNegociable) });
        }
        this.corridorAggregationService.updateServiceDistanceFromAppliedPrice(quoteService);
    }

    public applyGlobalDiscountAndRecalculate(quoteService: QuoteService): void {
        this.discountService.applyGlobalDiscount(quoteService);
        this.articlePricingService.copyArticleAppliedPriceToOldAppliedPrice(quoteService);
        this.calculateAppliedPrices(quoteService);
    }

    public calculateAppliedPrices(quoteService: QuoteService): void {
        const result = this.corridorAggregationService.sumDevicesAndConsumablesAppliedPrices(quoteService);
        if (!quoteService.globalCorridor) {
            return;
        }

        const appliedPrice = bigToString(result.appliedPriceDevices.add(result.appliedPriceConsumables));
        const appliedPriceOnlyNonNegociable = bigToString(
            result.appliedPriceOnlyNonNegociableConsumables.plus(result.appliedPriceOnlyNonNegociableDevices)
        );
        const appliedPriceOnlyNegociable = bigToString(new Big(appliedPrice).minus(
            result.appliedPriceOnlyNonNegociableConsumables.plus(result.appliedPriceOnlyNonNegociableDevices)
        ));
        const appliedPriceColor = this.corridorService.getMatchedColorByappliedPrice(quoteService.globalCorridor, appliedPrice, quoteService.nonNegociableArticleColor);
        const appliedPriceDevicesColor = quoteService.corridorDevices
            ? this.corridorService.getMatchedColorByappliedPrice(quoteService.corridorDevices, bigToString(result.appliedPriceDevices), quoteService.nonNegociableArticleColor)
            : undefined;
        const appliedPriceConsumablesColor = quoteService.corridorConsumables
            ? this.corridorService.getMatchedColorByappliedPrice(quoteService.corridorConsumables, bigToString(result.appliedPriceConsumables), quoteService.nonNegociableArticleColor)
            : undefined;

        Object.assign(quoteService, {
            appliedPrice,
            appliedPriceOnlyNegociable,
            appliedPriceOnlyNonNegociable,
            appliedPriceColor,
            appliedPriceDevices: bigToString(result.appliedPriceDevices),
            appliedPriceDevicesColor,
            appliedPriceConsumables: bigToString(result.appliedPriceConsumables),
            appliedPriceConsumablesColor
        });
    }

    public complete(quoteService: QuoteService, options: QuoteServiceLifecycleCompletionOptions = {}): void {
        if (options.resetDistance) {
            this.initQuoteServiceDistance(quoteService);
        }
        if (options.updateLastAppliedPriceState) {
            this.updateLastAppliedPriceState(quoteService);
        }
        if (options.updateLastContractInfoInputs) {
            this.updateLastContractInfoInputs(quoteService);
        }
    }

    public initQuoteServiceDistance(quoteService: QuoteService): void {
        if (!!quoteService.globalCorridor) {
            Object.assign(quoteService, { distance: null });
        }
    }

    public updateLastAppliedPriceState(quoteService: QuoteService): void {
        (quoteService.customGroups ?? []).forEach(customGroup => {
            (customGroup.pricingItems ?? []).forEach(pricingItem => {
                this.pricingItemPriceService.updateLastAppliedPriceState(pricingItem);
            });
        });
    }

    public updateLastContractInfoInputs(quoteService: QuoteService): void {
        Object.assign(quoteService, { lastContractInfoInputs: quoteService.contractInfoInputs });
    }
}

